import { useCallback } from 'react'
import { RELATIONSHIP_OPTIONS } from 'src/consumer/constants'
import { DEFAULT_PATIENT_DATA } from '../utils/patientFormConfig'
import {
  AGE_ENTRY_MODE_AGE,
  AGE_ENTRY_MODE_DOB,
  buildPatientAgeFields,
  buildPatientAgeFieldsFromDateOfBirth,
  getNormalizedAgeEntryMode,
  sanitizeAgeSecondaryValueInput,
  sanitizeAgeYearsInput,
  sanitizeDateOfBirthInput,
} from '../utils/patientAgeUtils'

export const usePatientForm = (
  patientDetails,
  setPatientDetails,
  makeAnonymousPatient,
  setMakeAnonymousPatient,
) => {
  const getCompositeAgeYearsValue = useCallback(
    (secondaryValue = patientDetails?.ageSecondaryValue) => {
      const existingAgeYears = sanitizeAgeYearsInput(patientDetails?.ageYears ?? patientDetails?.age)

      if (existingAgeYears !== '') {
        return existingAgeYears
      }

      return secondaryValue !== '' && secondaryValue !== null && secondaryValue !== undefined
        ? '0'
        : ''
    },
    [patientDetails?.age, patientDetails?.ageSecondaryValue, patientDetails?.ageYears],
  )

  const updatePatientField = useCallback(
    (field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        [field]: value,
      }))
    },
    [setPatientDetails],
  )

  const updatePatientFields = useCallback(
    (fields) => {
      setPatientDetails((prev) => ({
        ...prev,
        ...fields,
      }))
    },
    [setPatientDetails],
  )

  const updateNestedField = useCallback(
    (path, value) => {
      setPatientDetails((prev) => {
        const pathArray = path.split('.')
        const newState = { ...prev }
        let current = newState

        for (let i = 0; i < pathArray.length - 1; i++) {
          if (!current[pathArray[i]]) {
            current[pathArray[i]] = {}
          }
          current[pathArray[i]] = { ...current[pathArray[i]] }
          current = current[pathArray[i]]
        }

        current[pathArray[pathArray.length - 1]] = value
        return newState
      })
    },
    [setPatientDetails],
  )

  const handleMobileChange = useCallback(
    (value) => {
      updatePatientField('mobile', value)
    },
    [updatePatientField],
  )

  const handleNameChange = useCallback(
    (e) => {
      updatePatientField('name', e.target.value)
    },
    [updatePatientField],
  )

  const handleGenderChange = useCallback(
    (id) => {
      updatePatientField('gender', id)
    },
    [updatePatientField],
  )

  const handleAgeChange = useCallback(
    (e) => {
      const ageYears = sanitizeAgeYearsInput(e.target.value)
      updatePatientFields(
        buildPatientAgeFields({
          ageYears,
          ageSecondaryValue: patientDetails?.ageSecondaryValue,
          ageSecondaryUnit: patientDetails?.ageSecondaryUnit,
        }),
      )
    },
    [patientDetails?.ageSecondaryUnit, patientDetails?.ageSecondaryValue, updatePatientFields],
  )

  const handleAgeSecondaryValueChange = useCallback(
    (e) => {
      const ageSecondaryValue = sanitizeAgeSecondaryValueInput(e.target.value)
      updatePatientFields(
        buildPatientAgeFields({
          ageYears: getCompositeAgeYearsValue(ageSecondaryValue),
          ageSecondaryValue,
          ageSecondaryUnit: patientDetails?.ageSecondaryUnit,
        }),
      )
    },
    [getCompositeAgeYearsValue, patientDetails?.ageSecondaryUnit, updatePatientFields],
  )

  const handleAgeSecondaryUnitChange = useCallback(
    (e) => {
      updatePatientFields(
        buildPatientAgeFields({
          ageYears: getCompositeAgeYearsValue(),
          ageSecondaryValue: patientDetails?.ageSecondaryValue,
          ageSecondaryUnit: e.target.value,
        }),
      )
    },
    [getCompositeAgeYearsValue, patientDetails?.ageSecondaryValue, updatePatientFields],
  )

  // Actual date of birth: keeps the derived age fields in sync so every consumer of
  // `age` / `ageYears` (labels, prescriptions, payload) keeps working.
  const handleDobProvidedChange = useCallback(
    (e) => {
      const dobProvided = sanitizeDateOfBirthInput(e?.target?.value ?? e)
      const derivedAgeFields =
        (dobProvided && buildPatientAgeFieldsFromDateOfBirth(dobProvided)) ||
        buildPatientAgeFields({ ageYears: '', ageSecondaryValue: '', ageSecondaryUnit: '' })
      updatePatientFields({
        ...derivedAgeFields,
        ageEntryMode: AGE_ENTRY_MODE_DOB,
        dobProvided: e?.target?.value ?? dobProvided,
      })
    },
    [updatePatientFields],
  )

  const handleAgeEntryModeChange = useCallback(
    (mode) => {
      const nextMode = getNormalizedAgeEntryMode(mode)
      if (nextMode === AGE_ENTRY_MODE_AGE) {
        // Back to age: keep the (possibly derived) age values, drop the actual DOB.
        updatePatientFields({ ageEntryMode: AGE_ENTRY_MODE_AGE, dobProvided: '' })
        return
      }
      updatePatientFields({ ageEntryMode: AGE_ENTRY_MODE_DOB })
    },
    [updatePatientFields],
  )

  const handleEmailChange = useCallback(
    (e) => {
      updatePatientField('email', e.target.value.trim())
    },
    [updatePatientField],
  )

  const addAlternativeEmail = useCallback(() => {
    setPatientDetails((prev) => ({
      ...prev,
      altEmails: Array.isArray(prev?.altEmails) ? [...prev.altEmails, ''] : [''],
    }))
  }, [setPatientDetails])

  const updateAlternativeEmail = useCallback(
    (index, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        altEmails: prev?.altEmails?.map((altEmail, idx) => (idx === index ? value : altEmail)),
      }))
    },
    [setPatientDetails],
  )

  const removeAlternativeEmail = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        altEmails: prev?.altEmails?.filter((_, idx) => idx !== index),
      }))
    },
    [setPatientDetails],
  )

  const handleAddressChange = useCallback(
    (e) => {
      updatePatientField('address', e.target.value)
    },
    [updatePatientField],
  )

  const handleUhidChange = useCallback(
    (e) => {
      updatePatientField('uhId', e.target.value)
    },
    [updatePatientField],
  )

  const handleImportIdChange = useCallback(
    (e) => {
      updateNestedField('importData.importedFromId', e.target.value)
    },
    [updateNestedField],
  )

  // City picked from the city database (or typed): fills state/country when the option has them.
  const handleCitySelect = useCallback(
    (option) => {
      if (!option) {
        updatePatientFields({ city: '' })
        return
      }
      const city = (option.title || option.name || option.label || option.value || '').toString().trim()
      const fields = { city }
      if (option.administrativeEntityName) fields.state = option.administrativeEntityName
      if (option.country) fields.country = option.country
      if (option.countryCode) fields.countryCode = `${option.countryCode}`.toUpperCase()
      updatePatientFields(fields)
    },
    [updatePatientFields],
  )

  const handleStateChange = useCallback(
    (e) => {
      updatePatientField('state', e.target.value)
    },
    [updatePatientField],
  )

  const addTag = useCallback(
    (tag) => {
      if (!tag?.label) return
      setPatientDetails((prev) => {
        const existing = Array.isArray(prev?.tags) ? prev.tags : []
        const key = (tag.tagCode || tag.label).toString().trim().toLowerCase()
        const duplicate = existing.some(
          (item) => (item?.tagCode || item?.label || '').toString().trim().toLowerCase() === key,
        )
        if (duplicate) return prev
        return { ...prev, tags: [...existing, { ...tag, label: tag.label.trim() }] }
      })
    },
    [setPatientDetails],
  )

  const removeTag = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        tags: (Array.isArray(prev?.tags) ? prev.tags : []).filter((_, idx) => idx !== index),
      }))
    },
    [setPatientDetails],
  )

  const handleConsentChange = useCallback(
    (accepted, extra = {}) => {
      updatePatientField('consentDraft', {
        ...(extra || {}),
        accepted: Boolean(accepted),
        decidedAt: accepted ? new Date().toISOString() : null,
      })
    },
    [updatePatientField],
  )

  // Files picked before the patient exists; uploaded right after creation.
  const addPendingDocuments = useCallback(
    (stagedDocuments) => {
      const list = Array.isArray(stagedDocuments) ? stagedDocuments : [stagedDocuments]
      setPatientDetails((prev) => ({
        ...prev,
        pendingDocuments: [...(Array.isArray(prev?.pendingDocuments) ? prev.pendingDocuments : []), ...list.filter(Boolean)],
      }))
    },
    [setPatientDetails],
  )

  const updatePendingDocument = useCallback(
    (localId, patch) => {
      setPatientDetails((prev) => ({
        ...prev,
        pendingDocuments: (prev?.pendingDocuments || []).map((doc) =>
          doc.localId === localId ? { ...doc, ...patch } : doc,
        ),
      }))
    },
    [setPatientDetails],
  )

  const removePendingDocument = useCallback(
    (localId) => {
      setPatientDetails((prev) => ({
        ...prev,
        pendingDocuments: (prev?.pendingDocuments || []).filter((doc) => doc.localId !== localId),
      }))
    },
    [setPatientDetails],
  )

  const handleImportSourceChange = useCallback(
    (e) => {
      updateNestedField('importData.importedFrom', e.target.value)
    },
    [updateNestedField],
  )

  const handleCityChange = useCallback(
    (e) => {
      updatePatientField('city', e.target.value)
    },
    [updatePatientField],
  )

  const handlePincodeChange = useCallback(
    (e) => {
      updatePatientField('pincode', e.target.value)
    },
    [updatePatientField],
  )

  const handleNoteChange = useCallback(
    (e) => {
      updatePatientField('note', e.target.value)
    },
    [updatePatientField],
  )

  const handleReferredByChange = useCallback(
    (e) => {
      updatePatientField('referredBy', e.target.value)
    },
    [updatePatientField],
  )

  const handleLanguageChange = useCallback(
    (e) => {
      updatePatientField('defaultCommunicationLanguage', e.target.value)
    },
    [updatePatientField],
  )

  const handleRelationChange = useCallback(
    (e) => {
      updatePatientField('dependentRelation', e.target.value)
    },
    [updatePatientField],
  )

  const handleCommunicationToAttendantChange = useCallback(
    (event) => {
      updatePatientField('communicationToAttendant', event.target.checked)
    },
    [updatePatientField],
  )

  const handleAnonymousPatientChange = useCallback(
    (checked) => {
      setMakeAnonymousPatient(checked)
      if (checked) {
        updatePatientFields({
          email: '',
          mobile: '',
        })
      }
    },
    [setMakeAnonymousPatient, updatePatientFields],
  )

  // Attendant management. New rows default to the first relationship option so the value
  // always matches something the relation picker can show (the old 'self' default was not
  // a real option and rendered as a blank select).
  const addAttendant = useCallback(() => {
    setPatientDetails((prev) => {
      const existing = Array.isArray(prev?.attendants) ? prev.attendants : []
      return {
        ...prev,
        attendants: [
          ...existing,
          {
            name: '',
            primary: existing.length === 0,
            type: RELATIONSHIP_OPTIONS[0].value,
            mobile: '',
            email: '',
          },
        ],
      }
    })
  }, [setPatientDetails])

  const updateAttendant = useCallback(
    (index, field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        attendants: prev?.attendants?.map((attendant, idx) =>
          idx === index ? { ...attendant, [field]: value } : attendant,
        ),
      }))
    },
    [setPatientDetails],
  )

  const removeAttendant = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        attendants: prev?.attendants?.filter((_, idx) => idx !== index),
      }))
    },
    [setPatientDetails],
  )

  // Alternative mobile numbers management
  const addAlternativeMobile = useCallback(() => {
    setPatientDetails((prev) => ({
      ...prev,
      altMobiles: Array.isArray(prev?.altMobiles) ? [...prev.altMobiles, ''] : [''],
    }))
  }, [setPatientDetails])

  const updateAlternativeMobile = useCallback(
    (index, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        altMobiles: prev?.altMobiles?.map((altMobile, idx) => (idx === index ? value : altMobile)),
      }))
    },
    [setPatientDetails],
  )

  const removeAlternativeMobile = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        altMobiles: prev?.altMobiles?.filter((_, idx) => idx !== index),
      }))
    },
    [setPatientDetails],
  )

  // Communication preferences management
  const handleCommunicationToPrefChange = useCallback(
    (field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        communicationToPref: {
          toPatient: true,
          toAttendant: true,
          toOthers: true,
          receivers: [],
          ...prev?.communicationToPref,
          [field]: value,
        },
      }))
    },
    [setPatientDetails],
  )

  const addCommunicationReceiver = useCallback(() => {
    setPatientDetails((prev) => ({
      ...prev,
      communicationToPref: {
        toPatient: true,
        toAttendant: true,
        toOthers: true,
        ...prev?.communicationToPref,
        receivers: Array.isArray(prev?.communicationToPref?.receivers)
          ? [
              ...prev.communicationToPref.receivers,
              {
                toUserId: '',
                toEmail: '',
                toPhone: '',
                toWhatsAppId: '',
                toPatientId: '',
                authorizedBy: '',
                authorizedOn: '',
              },
            ]
          : [
              {
                toUserId: '',
                toEmail: '',
                toPhone: '',
                toWhatsAppId: '',
                toPatientId: '',
                authorizedBy: '',
                authorizedOn: '',
              },
            ],
      },
    }))
  }, [setPatientDetails])

  const updateCommunicationReceiver = useCallback(
    (index, field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        communicationToPref: {
          ...prev?.communicationToPref,
          receivers: prev?.communicationToPref?.receivers?.map((receiver, idx) =>
            idx === index ? { ...receiver, [field]: value } : receiver,
          ),
        },
      }))
    },
    [setPatientDetails],
  )

  const removeCommunicationReceiver = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        communicationToPref: {
          ...prev?.communicationToPref,
          receivers: prev?.communicationToPref?.receivers?.filter((_, idx) => idx !== index),
        },
      }))
    },
    [setPatientDetails],
  )

  const resetForm = useCallback(() => {
    setPatientDetails(DEFAULT_PATIENT_DATA)
    setMakeAnonymousPatient(false)
  }, [setPatientDetails, setMakeAnonymousPatient])

  const getFieldValue = useCallback(
    (fieldPath) => {
      const pathArray = fieldPath.split('.')
      let value = patientDetails

      for (const path of pathArray) {
        value = value?.[path]
        if (value === undefined || value === null) break
      }

      return value
    },
    [patientDetails],
  )

  return {
    // Field update handlers
    updatePatientField,
    updatePatientFields,
    updateNestedField,

    // Specific field handlers
    handleMobileChange,
    handleNameChange,
    handleGenderChange,
    handleAgeChange,
    handleAgeSecondaryValueChange,
    handleAgeSecondaryUnitChange,
    handleDobProvidedChange,
    handleAgeEntryModeChange,
    handleEmailChange,
    addAlternativeEmail,
    updateAlternativeEmail,
    removeAlternativeEmail,
    handleAddressChange,
    handleUhidChange,
    handleImportIdChange,
    handleCityChange,
    handleCitySelect,
    handleStateChange,
    addTag,
    removeTag,
    handleConsentChange,
    handleImportSourceChange,
    addPendingDocuments,
    updatePendingDocument,
    removePendingDocument,
    handlePincodeChange,
    handleNoteChange,
    handleReferredByChange,
    handleLanguageChange,
    handleRelationChange,
    handleCommunicationToAttendantChange,
    handleAnonymousPatientChange,

    // Array field management
    addAttendant,
    updateAttendant,
    removeAttendant,
    addAlternativeMobile,
    updateAlternativeMobile,
    removeAlternativeMobile,
    handleCommunicationToPrefChange,
    addCommunicationReceiver,
    updateCommunicationReceiver,
    removeCommunicationReceiver,

    // Utility functions
    resetForm,
    getFieldValue,
  }
}
